import { Link } from "@tanstack/react-router";
import { useT } from "../i18n";
import { useSitzung } from "./Sitzung";
import { useRuf } from "./Ruf";
import { mehrFuer } from "./handywege";
import { seitenleisteFuer, ThemaLeiste } from "./Leiste";
import { Seitenkopf } from "../bausteine/Handyformen";
import { Fassung } from "../bausteine/Fassung";
import { diagnoseSetzen, useDiagnose } from "../bausteine/Touchdiagnose";
import { Zeichen, type Zeichenname } from "../bausteine/Zeichen";
import "../stile/handy-leiste.css";

/* ══════════════════════════════════════════════════════════════════
   „Mehr" — ein Blatt von unten, kein Weg (17.09.2026).

   Bis hierher war „Mehr" eine eigene Maske unter `/handy/mehr`. Wer darauf
   tippte, verließ die Maske, auf der er stand: Adresse, Rollstand und ein
   halb ausgefülltes Formular waren weg, und zurück ging es über einen
   zweiten Schritt im Verlauf. 40-mobiles-grundgeruest.md (Frage 2)
   begründet das Blatt: **Die Maske darunter behält Zustand und Adresse,
   und das Blatt schließt mit der Zurückgeste.** Material, Apple und NN/g
   sagen dazu dasselbe — in „Mehr" steht das Seltene, und es soll sich
   öffnen, ohne dass man woanders ist.

   **Seit dem 17.09.2026 ist es auch die einzige zweite Tür.** Menüknopf und
   Schublade sind am Telefon weggefallen (Schritt 1); was die Seitenleiste
   trug, steht hier: die Wege der Rolle, die Punkte der Seitenleiste in
   ihrer Gliederung, die Darstellung (System · Hell · Dunkel), die große
   Ansicht und das Abmelden. Welche Wege das sind, entscheiden
   `handywege.ts` und `Leiste.tsx` — dieselben Tabellen, aus denen Leiste
   und Seitenleiste lesen.

   `/handy/mehr` gibt es weiter, als Umleitung: Ein Lesezeichen oder ein
   installiertes Telefon, das die Adresse kennt, landet auf dem Einstieg
   der Rolle, und das Blatt steht offen (`router.tsx`).
   ══════════════════════════════════════════════════════════════════ */

/* **Signal (Paket 5, 07.10.2026): „Mehr" ist wieder eine Seite.** Das Blatt
   vom 17.09.2026 (oben begründet) hielt die Maske darunter stehen — dafür
   sprang das aktive Fach auf „Mehr", das Blatt verdeckte Leiste und Knopf,
   und es gab keinen eigenen Stand je Fach. Das Muster (app-design-muster,
   `fassungen/telefon/navigation.md` § 2) und Lucas Entscheidung F5 vom
   07.10.2026: „Mehr" ist eine Wurzelseite mit Liste. Den Stand der Maske, von
   der man kommt, hält jetzt die Leiste fest (`Handyleiste.tsx`, „Jedes Fach
   behält seinen Stand") — ein Tipp auf das alte Fach führt dorthin zurück. */

type Weg = { weg: string; text: string; satz?: string | undefined; zeichen?: Zeichenname | undefined };

/* ══════════════════════════════════════════════════════════════════
   Was die Seitenleiste führt, „Mehr" am Telefon aber nicht — 18.09.2026.

   Luca, beim Durchklicken der Handy-Ansicht: *„Das, was wirklich bescheuert
   aussieht, ist die Planung. Die stell hinten an. Das soll auf Mobile nicht
   zur Verfügung stehen. In Step 1 machen wir das später an."*

   **Die Liste steht hier und nicht in `Leiste.tsx`**, weil dort die
   Navigation des Schreibtischs steht: Das Brett ist die Hauptarbeit der
   Disposition, und es bleibt, wo es ist. „Mehr" gibt es nur am Telefon
   (`schale/Handyleiste.tsx`) — also ist das hier die Stelle, an der ein Weg
   am Telefon verschwindet, ohne dass ihn irgendjemand sonst verliert.

   **Ein Weg, der aus der Wegweisung fällt, fällt nicht aus der Anwendung.**
   `/planung` bleibt eine gültige Adresse und leitet am Telefon dauerhaft auf
   `/start` um (`router.tsx`). Ohne diese Liste stünde der Punkt hier weiter
   und führte auf die Startseite zurück — ein Menüpunkt, der nichts tut, ist
   schlimmer als keiner (28-designkonzept.md, „Die fünf Zustände": *Kein
   Recht* — der Punkt fehlt, statt zu erscheinen und zu meckern).

   Kommt die Planung am Telefon wieder („In Step 1 machen wir das später
   an"), ist es diese eine Zeile.
   ══════════════════════════════════════════════════════════════════ */
/* Seit dem 08.10.2026 (docs/65): Kalender und Einteilen unter „Disposition"
   — beide sind Bretter für den Schreibtisch. */
const AM_TELEFON_NICHT = new Set(["/kalender", "/einteilen"]);

/** Eine Liste von Wegen — dieselbe Form wie auf dem Startschirm des Verkaufs (`hwege`). */
function Wege({ wege }: { wege: Weg[] }) {
  const t = useT();
  if (!wege.length) return null;
  return (
    <div className="hwege">
      {wege.map((w) => (
        <Link
          key={w.weg}
          to={w.weg}
          className={w.zeichen ? "hweg-zeile" : "hweg-zeile ohne-zeichen"}
          /* Einzeilig wie im Plus-Blatt (19.09.2026) — der Satz steht im
             `title`, nicht als zweite Zeile. */
          title={w.satz ? t(w.satz) : undefined}
        >
          {w.zeichen ? <Zeichen name={w.zeichen} /> : null}
          <b>{t(w.text)}</b>
          <Zeichen name="weiter" />
        </Link>
      ))}
    </div>
  );
}

export function MehrSeite() {
  const t = useT();
  const diagnose = useDiagnose();
  const { ich, abmelden, darfAuf } = useSitzung();
  const ruf = useRuf();

  const wege = mehrFuer(ich?.rollen ?? []);
  /* Dieselben Gruppen wie in der Seitenleiste, ohne die Wege, die es am
     Telefon nicht gibt. Eine Gruppe, aus der dabei alles herausfällt,
     verschwindet mit — eine Überschrift ohne Punkte ist keine Gruppe. */
  const seitenleiste = seitenleisteFuer(darfAuf)
    .map((g) => ({ ...g, punkte: g.punkte.filter((p) => !AM_TELEFON_NICHT.has(p.weg)) }))
    .filter((g) => g.punkte.length > 0);

  return (
    <div className="handy mehr-seite">
      <Seitenkopf titel={t("handyLeiste.mehr")} />
      <div className="mehr-blatt">
        <Wege wege={wege} />

        {seitenleiste.map((g) => (
          <section className="mehr-gruppe" key={g.gruppe ?? g.punkte[0]?.weg}>
            {g.gruppe ? <h4>{t(g.gruppe)}</h4> : null}
            <Wege wege={g.punkte} />
          </section>
        ))}

        {/* **Das Handbuch** (21.09.2026). Es steht nicht in der Seitenleiste
            und kommt deshalb nicht über `seitenleisteFuer` mit — am
            Schreibtisch hängt es im Benutzermenü (`Leiste.tsx`), und das gibt
            es am Telefon nicht. Also hier, als eigene Zeile: **ohne Recht
            davor**, wie am Schreibtisch. Ein Weg, kein Schalter — deshalb
            über und nicht in `mehr-schluss`. */}
        <section className="mehr-gruppe">
          <Wege wege={[
            { weg: "/handbuch", text: "nav.handbuch", satz: "handbuch.satz" },
            // Mein Konto (E4, 04.10.2026): der eigene zweite Faktor.
            { weg: "/konto", text: "nav.konto", satz: "konto.satz" },
          ]} />
        </section>

        <section className="mehr-gruppe">
          <h4>{t("thema.schild")}</h4>
          <ThemaLeiste />
        </section>

        {/* **„Große Ansicht" ist am 20.09.2026 entfallen.** Sie schaltete
            seit dem 17.09. nichts mehr: Jede Maske trägt sich am Telefon
            selbst (`TELEFONTAUGLICH`), der Wegwächter schickte also
            ohnehin niemanden mehr weg, und `/planung` fragte den Schalter
            nie. Luca: *„Große Ansicht rausnehmen, wenn das nichts bringt."*
            Das Abmelden ist eine Handlung, kein Weg — es steht abgesetzt
            und nicht in derselben Fläche wie die Wege darüber. */}
        <div className="mehr-schluss">
          {/* **Die Touch-Diagnose** (20.09.2026): Ein Mitschrieb, der sagt,
              was ein Tipp trifft — für den Fehler, der nur auf dem Gerät
              auftritt und in keinem Simulator. Sie gilt für diesen Reiter,
              nicht länger. */}
          <button
            type="button"
            className="mehr-knopf"
            aria-pressed={diagnose}
            onClick={() => { diagnoseSetzen(!diagnose); }}
          >
            <Zeichen name="schaden" />
            {t(diagnose ? "handyLeiste.diagnoseAus" : "handyLeiste.diagnoseAn")}
          </button>
          <button
            type="button"
            className="mehr-knopf"
            onClick={() => { void abmelden(); ruf("abgemeldet", undefined, "acht"); }}
          >
            <Zeichen name="abmelden" />
            {t("benutzer.abmelden")}
          </button>
        </div>

        {/* Wer einen Fehler meldet, nennt die Fassung — und am Telefon ist
            dieses Blatt die Stelle, an der man sucht. */}
        <p className="mehr-fuss">
          {ich ? `${ich.name} ${t("allgemein.trennpunkt")} ` : ""}<Fassung stand />
        </p>
      </div>
    </div>
  );
}
